import { gql, useLazyQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import useProcessData from "../../../../CommonFunction/ProcessData";
import { LoadTable, Loading } from "../../../Components";
import Button from "../../../Components/Button/Button";
import HeaderDiv from "../../../Components/HeaderDiv/HeaderDiv";
import Input from "../../../Components/Input/Input";
import { ErrorToast } from "../../../Components/ToastComponent/RectHotToast";
import useFetchTableData from "../../../custom_hooks/useFetchTableData";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useGetPrintReport } from "../../../custom_hooks/useGetPrintReport";

const queryfunction = (fields) => {
  const query = gql`
    query RefrencesBillByBill($type: String!) {
      RefrencesBillByBill(Type: $type) {
        _id
        ${fields?.join(" ")}
      }
    }
  `;
  return query;
};

const queryTwoFunction = (fields) => {
  const query = gql`
    query ReferencesBillByBillWithGrouping($type: String, $settledStatus: Boolean) {
      ReferencesBillByBillWithGrouping(Type: $type, settled_Status: $settledStatus) {
        _id
        ${fields?.join(" ")}
      }
    }
  `;
  return query;
};

const ReceivableBillByBill = () => {
  const [formData, setFormData] = useState([]);
  const [isLoader, setIsLoader] = useState(true);
  const colAddition = ["Credit_Amount", "Debit_Amount"];
  const [selectedValue, setSelectedValue] = useState("Party wise");
  const [TableTitle,setTableTitle]=useState("Receivable")
  const [selectedKey, setSelectedKey] = useState("Receivable");
  const [selectRefvalue, setSelectedRefvalue] = useState("Party wise");
  const [settleStatus, setSettleStatus] = useState(true);
const { fetchReport, report,  } = useGetPrintReport();
  useEffect(() => {
    if (selectedValue === "Party wise") {
      setSelectedKey("Receivable");
    } else if (selectedValue === "All bill by bill") {
      setSettleStatus(true);
      setSelectedKey("AllReceivableBillWithParty");
    } else if (selectedValue === "Party with bills") {
      setSettleStatus(false);
      setSelectedKey("ReceivableBillWithParty");
    }
  }, [selectedValue]);

  const [fetchReceivableBillByBill, { loading: queryLoading, error, data }] = useLazyQuery(
    selectRefvalue === "Party wise" ? queryfunction([]) : queryTwoFunction([], settleStatus),
    {
      variables: {
        type: "Receivable",
        ...(selectRefvalue === "All bill by bill"
          ? { settledStatus: true }
          : selectRefvalue === "Party with bills"
          ? { settledStatus: false }
          : {}),
      },
      fetchPolicy: "network-only",
      onCompleted: () => {
        setIsLoader(false);
      },
      onError: () => {
        setIsLoader(false);
      },
    }
  );

  const handleChange = (e) => {
    setSelectedValue(e.target.value);
  };

  const handleGo = () => {
    setTableTitle(selectedKey)
    if (selectedValue !== selectRefvalue) {
      setFormData([]);
      setSelectedRefvalue(selectedValue);
    }
  };
 
  const config = useMemo(
    () => ({
      dataKey: selectRefvalue === "Party wise" ? "RefrencesBillByBill" : "ReferencesBillByBillWithGrouping",
      dateKeys: ["createdAt", "updatedAt"],
      deleteKeys: ["is_Audit", "__typename"],
    }),
    [selectRefvalue]
  );

  const { loading: tableDataLoading, TableHeaders } = useFetchTableData(
    selectRefvalue === "Party wise" ? queryfunction : queryTwoFunction,
    fetchReceivableBillByBill,
    setIsLoader,
    selectRefvalue === "Party wise"
      ? "Receivable"
      : selectRefvalue === "All bill by bill"
      ? "AllReceivableBillWithParty"
      : "ReceivableBillWithParty"
  );

  const { loading: processDataLoading } = useProcessData(data, setFormData, config, error);
  async function handleExportxl(data) {
    const company   = report?.Company_Name || "";
    const totalCols = 8; // Date, UniqueId, Particular, Debit, Credit, Final Closing, Credit Day, Due Days

    // 1️⃣ Workbook & worksheet
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Receivables");

    // 2️⃣ Title row (A1→H1)
    ws.mergeCells(1, 1, 1, totalCols);
    const t = ws.getCell("A1");
    t.value     = company;
    t.font      = { size: 16, bold: true };
    t.alignment = { horizontal: "center", vertical: "middle" };
    ws.getRow(1).height = 24;

    // 3️⃣ Column definitions (no auto-header)
    ws.columns = [
      { key: "Date",           width: 15 },
      { key: "UniqueId",       width: 20 },
      { key: "Particular",     width: 45 },
      { key: "Total_Debit",    width: 14 },
      { key: "Total_Credit",   width: 14 },
      { key: "Closing_Amount",  width: 14 },
      { key: "Credit_Days",    width: 12 },
      { key: "Due_Days",       width: 12 },
    ];

    // 4️⃣ Header row in Row 2
    const headers = [
      "Date", "UniqId", "Particular",
      "Debit", "Credit", "Final Closing",
      "Credit Day", "Due Days"
    ];
    const hRow = ws.getRow(2);
    hRow.values = headers;
    hRow.eachCell(cell => {
      cell.font      = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "FF666666" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border    = { bottom: { style: "thin", color: { argb: "FF444444" } } };
    });
    hRow.height = 18;
    hRow.commit();

    // 5️⃣ Group data by party
    const groups = data.reduce((acc, r) => {
      (acc[r.Account_Name] ||= []).push(r);
      return acc;
    }, {});

    // 6️⃣ Render each group
    for (const [party, rows] of Object.entries(groups)) {
      // • Party header (light grey)
      const pRow = ws.addRow([`Party: ${party}`, ...Array(totalCols - 1).fill("")]);
      pRow.eachCell(c => {
        c.font      = { bold: true };
        c.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "FFCCCCCC" } };
        c.alignment = { horizontal: "left", vertical: "middle" };
      });
      pRow.commit();
      ws.mergeCells(pRow.number,1,pRow.number,totalCols);

      // • Detail rows
      rows.forEach(r => {
        const dr = ws.addRow([
          r.Date,
          r.Unique_Id,
          r.Particular,
          r.Total_Debit,
          r.Total_Credit,
          r.Closing_Amount,
          r.Credit_Days,
          r.Due_Days,
        ]);
        dr.eachCell((c, idx) => {
          if (idx >= 4 && idx <= 8) {
            c.alignment = { horizontal: "right", vertical: "middle" };
          } else {
            c.alignment = { horizontal: "left",  vertical: "middle" };
          }
        });
        dr.commit();
      });

      // • Summary row (same grey fill as party header)
      const sumD   = rows.reduce((s,r) => s + (+r.Total_Debit   || 0), 0);
      const sumC   = rows.reduce((s,r) => s + (+r.Total_Credit  || 0), 0);
      const sumF   = rows.reduce((s,r) => s + (+r.Closing_Amount || 0), 0);
      const   count = rows.length || 0 ;
      const sr = ws.addRow([
      count, "", "",
        sumD, sumC, sumF,
        "", ""
      ]);
      sr.eachCell((c, idx) => {
        if (idx >= 4 && idx <= 6) {
          c.font      = { bold: true };
          c.numFmt    = "#,##0.00";
          c.alignment = { horizontal: "right", vertical: "middle" };
        }
        // apply same fill as party row
        c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFCCCCCC" } };
      });
      sr.commit();
    }

    // 7️⃣ Export with styles
    const buf = await wb.xlsx.writeBuffer({ useStyles: true, useSharedStrings: true });
    saveAs(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      "Receivable_BillByBill.xlsx"
    );
  }

  useEffect(() => {
    if (error) {
      ErrorToast(error.message);
    }
  }, [error]);

  const isLoading = queryLoading || processDataLoading || isLoader || tableDataLoading;

  const conditionalProps = {
    ...(selectRefvalue !== "Party wise" && {
      InitialGrouping: true,
      GroupValues: ["Account_Name"],
    }),
  };
  console.log(formData, "selectedKeyselectedKeyselectedKeyselectedKey");

  return (
    <div>
      <div className="Container_For_Receivable_Sales_Report">
        <div className="Page_Title_Container_For_Receivable_Sales_Report">{"Receivable (Bill By Bill)"}</div>
        <div className="Receivable_Sales_Report_Search_Box_Container">
          <div className="Receivable_Sales_Report_Search_Box">
            <Input
              EMTQuery=""
              checked=""
              InputConfig={{}}
              type="Dropdown"
              name="Voucher_Type"
              dropdowndata={["Party wise", "Party with bills", "All bill by bill"]}
              handleBlur={() => {}}
              handleInputChange={handleChange}
              label={"Select voucher type"}
              modalform={true}
              value={selectedValue}
              notSearchable={true}
            />
          </div>
          <div className="Receivable_Sales_Report_Go_Button">
            <Button name="gobutton" text="GO" action={handleGo}></Button>
          </div>
          <div className="Receivable_Sales_Report_Header">
            <HeaderDiv
              TableKey={TableTitle}
              IsDateSearchShow={true}
              MoreActions={{
                Is_Add: false,
                Is_Edit: false,
                Is_Delete: false,
                Is_View: false,
              }}
            />
          </div>
        </div>
      </div>
      <div>
        {isLoader ? (
          <Loading />
        ) : !formData?.length ? (
          <p className="DataNotAvailable">Data not Available</p>
        ) : (
          <>
           {(queryLoading || isLoading) && <div className="loader-line-div" />}
            <LoadTable
              title={TableTitle}
              data={formData}
              error={error}
              colAddition={colAddition}
              TableHeaders={TableHeaders}
              onExportExcel={handleExportxl}
              resetFiltersOnUnmount={true}
              {...conditionalProps}
              Tab=""
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ReceivableBillByBill;
